import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import { useAgentLog, useAgentSessions, useOverrideAction } from '../hooks/useOps';
import type { AgentActionLog, SessionSummary } from '../api/types';
import { formatDate, humanizeStatus } from '../lib/format';

const STATUS_COLOR: Record<string, 'default' | 'success' | 'error' | 'warning' | 'info'> = {
  success: 'success',
  failed: 'error',
  rejected: 'error',
  pending: 'warning',
  pending_approval: 'info',
};

export function AgentActivityPage() {
  const [status, setStatus] = useState('');
  const [tier, setTier] = useState('');
  const [agentId, setAgentId] = useState('');
  const { data, isLoading, error } = useAgentLog({ status, tier, agentId, limit: 100 });
  const { data: sessions = [] } = useAgentSessions();
  const override = useOverrideAction();

  const markOverridden = async (row: AgentActionLog) => {
    if (!window.confirm(`Mark "${row.actionType}" as overridden (corrected/undone by you)?`)) return;
    await override.mutateAsync(row.id);
  };

  const sessionColumns: GridColDef<SessionSummary>[] = [
    { field: 'agentId', headerName: 'Session', minWidth: 160, flex: 1 },
    { field: 'agentKeyName', headerName: 'Key', width: 150, valueGetter: (v) => v ?? 'legacy' },
    {
      field: 'lastSeenAt',
      headerName: 'Last seen',
      width: 130,
      valueGetter: (v) => formatDate(v as string),
    },
    { field: 'actionCount', headerName: 'Actions', width: 90, align: 'right', headerAlign: 'right' },
    {
      field: 'approvalCount',
      headerName: 'Approvals',
      width: 95,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'rejectedCount',
      headerName: 'Rejected',
      width: 90,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) =>
        p.value > 0 ? <Chip size="small" color="error" variant="outlined" label={p.value} /> : '0',
    },
  ];

  const columns: GridColDef<AgentActionLog>[] = [
    { field: 'createdAt', headerName: 'When', width: 130, valueGetter: (v) => formatDate(v as string) },
    { field: 'agentId', headerName: 'Session', width: 140 },
    { field: 'agentKeyName', headerName: 'Key', width: 130, valueGetter: (v) => v ?? 'legacy' },
    { field: 'actionType', headerName: 'Action', minWidth: 160, flex: 1, valueGetter: (v) => humanizeStatus(v as string) },
    { field: 'tier', headerName: 'Tier', width: 110, valueGetter: (v) => humanizeStatus(v as string) },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (p) => (
        <Chip size="small" label={humanizeStatus(p.value)} color={STATUS_COLOR[p.value] ?? 'default'} variant="outlined" />
      ),
    },
    {
      field: 'executionTimeMs',
      headerName: 'ms',
      width: 70,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (v) => v ?? '—',
    },
    {
      field: 'wasOverridden',
      headerName: 'Overridden',
      width: 105,
      renderCell: (p) => (p.value ? <Chip size="small" color="warning" label="Yes" /> : '—'),
    },
    {
      field: 'detail',
      headerName: 'Detail',
      minWidth: 200,
      flex: 1,
      sortable: false,
      valueGetter: (_v, row) => row.errorMessage ?? row.reasoning ?? '—',
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (p) =>
        p.row.status === 'success' && p.row.tier !== 'read' && !p.row.wasOverridden ? (
          <Tooltip title="Mark as overridden (a human corrected this)">
            <Button size="small" onClick={() => markOverridden(p.row)} aria-label="override">
              <UndoIcon fontSize="small" />
            </Button>
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Agent activity"
        action={
          <Stack direction="row" gap={1}>
            <TextField select size="small" label="Tier" value={tier} onChange={(e) => setTier(e.target.value)} sx={{ width: 150 }}>
              {['', 'read', 'auto_write', 'approval_gated'].map((t) => (
                <MenuItem key={t || 'all'} value={t}>
                  {t ? humanizeStatus(t) : 'All tiers'}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ width: 160 }}>
              {['', 'success', 'failed', 'rejected', 'pending', 'pending_approval'].map((s) => (
                <MenuItem key={s || 'all'} value={s}>
                  {s ? humanizeStatus(s) : 'All statuses'}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      {sessions.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Sessions — click one to filter the feed (a session id is also the Hermes session for link-back)
          </Typography>
          <Paper variant="outlined" sx={{ height: 230 }}>
            <DataGrid
              rows={sessions}
              columns={sessionColumns}
              getRowId={(r) => r.agentId}
              density="compact"
              hideFooter
              disableColumnMenu
              onRowClick={(p) => setAgentId(p.row.agentId)}
              sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
            />
          </Paper>
        </Box>
      )}

      {agentId && (
        <Chip
          sx={{ mb: 2 }}
          color="primary"
          label={`Filtered to session: ${agentId}`}
          onDelete={() => setAgentId('')}
        />
      )}

      <Paper variant="outlined" sx={{ height: 620 }}>
        <DataGrid
          rows={data?.data ?? []}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Paper>
    </Box>
  );
}
